import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useCart } from "../lib/cart";
import { formatGBP } from "../lib/format";
import { Package, Storefront, CheckCircle, Clock, TruckTrailer, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_LABELS = {
  pending: { label: "Pending", color: "bg-[#E4A951]/15 text-[#8A6A1F] border-[#E4A951]/40" },
  accepted: { label: "Accepted", color: "bg-[#2B5329]/10 text-[#2B5329] border-[#2B5329]/30" },
  preparing: { label: "Preparing", color: "bg-[#A94438]/10 text-[#A94438] border-[#A94438]/30" },
  dispatched: { label: "Dispatched", color: "bg-[#1A1A18]/10 text-[#1A1A18] border-[#1A1A18]/30" },
  delivered: { label: "Delivered", color: "bg-[#2B5329]/10 text-[#2B5329] border-[#2B5329]/30" },
  cancelled: { label: "Cancelled", color: "bg-[#5C5C54]/10 text-[#5C5C54] border-[#5C5C54]/30" },
};

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { add } = useCart();
  const navigate = useNavigate();

  const handleReorder = async (cartOrders) => {
    try {
      let added = 0;
      for (const o of cartOrders) {
        const { data: full } = await api.get(`/orders/${o.order_id}`);
        for (const item of (full.items || [])) {
          try {
            const { data: product } = await api.get(`/products/${item.product_id}`);
            if (product && product.active && product.stock_quantity > 0) {
              const qty = Math.min(item.quantity, product.stock_quantity);
              add(product, qty);
              added += 1;
            }
          } catch (err) { /* eslint-disable-next-line no-console */ console.warn("[MyOrders] non-fatal error:", err); }
        }
      }
      if (added > 0) {
        toast.success(`${added} item${added === 1 ? "" : "s"} added to your basket`);
        navigate("/cart");
      } else {
        toast.error("No items from this order are available right now");
      }
    } catch {
      toast.error("Could not reorder");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/orders/mine");
        setOrders(data);
      } catch (err) { /* eslint-disable-next-line no-console */ console.warn("[MyOrders] non-fatal error:", err); }
      setLoading(false);
    })();
  }, []);

  // Group by parent_cart_id
  const grouped = orders.reduce((acc, o) => {
    acc[o.parent_cart_id] = acc[o.parent_cart_id] || { parent_cart_id: o.parent_cart_id, created_at: o.created_at, orders: [] };
    acc[o.parent_cart_id].orders.push(o);
    return acc;
  }, {});
  const carts = Object.values(grouped).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div className="pt-24 pb-24 max-w-5xl mx-auto px-4 md:px-8" data-testid="my-orders-page">
      <h1 className="font-heading text-3xl md:text-5xl font-black tracking-tighter mb-8">My orders</h1>

      {loading ? (
        <div className="space-y-4">{[0, 1].map((i) => <div key={i} className="h-32 bg-white rounded-2xl animate-pulse border border-[#E5E2D9]" />)}</div>
      ) : carts.length === 0 ? (
        <div className="card-surface p-10 text-center" data-testid="no-orders">
          <Package size={40} weight="duotone" className="text-[#5C5C54] mx-auto mb-4" />
          <p className="text-sm text-[#5C5C54] mb-4">No orders yet.</p>
          <Link to="/" className="btn-primary">Start shopping</Link>
        </div>
      ) : (
        <div className="space-y-6">
          {carts.map((cart) => (
            <div key={cart.parent_cart_id} className="card-surface overflow-hidden" data-testid="cart-order-group">
              <div className="px-5 py-3 bg-[#F9F7F3] border-b border-[#E5E2D9] flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs text-[#5C5C54]">Placed {new Date(cart.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
                  <p className="text-xs text-[#5C5C54]">{cart.orders.length} vendor{cart.orders.length === 1 ? "" : "s"}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <p className="font-heading font-black text-lg">{formatGBP(cart.orders.reduce((a, o) => a + o.total, 0))}</p>
                  <button
                    data-testid="reorder-btn"
                    onClick={() => handleReorder(cart.orders)}
                    className="btn-secondary text-xs gap-1"
                    title="Add all items back to your basket"
                  >
                    <ArrowClockwise size={14} weight="bold" /> Buy again
                  </button>
                </div>
              </div>
              <div className="divide-y divide-[#E5E2D9]">
                {cart.orders.map((o) => {
                  const s = STATUS_LABELS[o.status] || { label: o.status, color: "bg-[#F2EFE9]" };
                  return (
                    <Link to={`/orders/${o.order_id}`} key={o.order_id} className="p-5 flex items-center gap-4 hover:bg-[#F9F7F3] transition-colors" data-testid="order-row">
                      <div className="w-10 h-10 rounded-full bg-[#F2EFE9] flex items-center justify-center flex-shrink-0">
                        <Storefront size={18} weight="duotone" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-heading font-bold text-sm truncate">{o.vendor_name}</p>
                        <p className="text-xs text-[#5C5C54]">{formatGBP(o.total)} · #{o.order_id.slice(-8)}</p>
                      </div>
                      <span className={`chip ${s.color}`}>{s.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
